import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home as HomeIcon, Phone, MessageCircle, Navigation, PlayCircle, CheckCircle2,
  Wallet, Loader2, ChevronDown, XCircle, AlertTriangle, ArrowRightLeft, Clock,
  PlusCircle, RefreshCw
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, telLink, waLink, todayIST, fmtDate, initials } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge, AccountBalanceBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const MODALITIES = ["Manual Therapy", "Exercise Therapy", "IFT", "TENS", "Ultrasound", "Dry Needling", "Cupping", "Traction", "Posture Correction", "Gait Training"];

export default function HomeVisits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayIST());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Modals
  const [completeVisit, setCompleteVisit] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    pain_score: 5,
    treatment: "",
    modalities: [],
    duration: 45,
    exercises: "",
    notes: "",
    next_visit: "",
  });

  const [providerCancelVisit, setProviderCancelVisit] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReschedDate, setCancelReschedDate] = useState("");
  const [cancelReplacementPhysio, setCancelReplacementPhysio] = useState("");
  const [physios, setPhysios] = useState([]);

  const [payVisit, setPayVisit] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payType, setPayType] = useState("session");
  const [payLoading, setPayLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/home-visits/today", { params: { date } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/staff").then((res) => {
        const ph = (res.data.items || []).filter((u) => u.role === "physiotherapist");
        setPhysios(ph);
      }).catch(() => {});
    }
  }, [user]);

  const handleStart = async (v) => {
    setBusy(v.id);
    try {
      await api.post(`/home-visits/${v.id}/start`);
      toast.success("Visit started");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const handleCompleteSubmit = async () => {
    if (!completeVisit) return;
    setBusy(completeVisit.id);
    try {
      const res = await api.post(`/home-visits/${completeVisit.id}/complete`, completeForm);
      toast.success(res.data.message || "Visit completed");
      setCompleteVisit(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const handleProviderCancelSubmit = async () => {
    if (!providerCancelVisit) return;
    if (!cancelReason || cancelReason.trim().length < 3) {
      toast.error("Cancellation reason is mandatory");
      return;
    }
    setBusy(providerCancelVisit.id);
    try {
      await api.post(`/home-visits/${providerCancelVisit.id}/provider-cancel`, {
        reason: cancelReason.trim(),
        replacement_physio_id: cancelReplacementPhysio || null,
        rescheduled_date: cancelReschedDate || null,
      });
      toast.success("Visit marked provider cancelled");
      setProviderCancelVisit(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const handlePatientUnavailable = async (v) => {
    const reason = window.prompt("Reason for patient unavailability:", "Patient not at home / unreachable");
    if (reason === null) return;
    try {
      await api.post(`/home-visits/${v.id}/status`, {
        status: "patient_unavailable",
        reason: reason || "Patient unavailable",
      });
      toast.success("Marked patient unavailable");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handlePatientCancel = async (v) => {
    const reason = window.prompt("Reason for patient cancellation:", "Patient requested cancellation");
    if (reason === null) return;
    try {
      await api.post(`/home-visits/${v.id}/status`, {
        status: "patient_cancelled",
        reason: reason || "Patient cancelled",
      });
      toast.success("Marked patient cancelled");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handlePaySubmit = async () => {
    if (!payVisit || !payAmount || Number(payAmount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    setPayLoading(true);
    try {
      await api.post("/payments", {
        patient_id: payVisit.patient_id,
        amount: Number(payAmount),
        method: payMethod,
        type: payType,
        service: "home",
        collected_by: user?.role === "physiotherapist" ? "physiotherapist" : "owner",
        notes: `Home visit collection ${payVisit.date}`,
      });
      toast.success("Payment recorded successfully");
      setPayVisit(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={user?.role === "physiotherapist" ? "My Home Visits" : "Home Visits Operations"}
        subtitle={fmtDate(date)}
        testid="homevisits-page"
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40 h-8 text-xs bg-white"
              data-testid="hv-date"
            />
          </div>
        }
      />

      {loading ? (
        <Loading label="Loading home visits..." />
      ) : items.length === 0 ? (
        <EmptyState
          icon={HomeIcon}
          title="No home visits for this date"
          subtitle="Visits are schedule-aware and generated for active home patients."
        />
      ) : (
        <div className="grid gap-3.5 lg:grid-cols-2">
          {items.map((v) => (
            <Card key={v.id} data-testid={`visit-card-${v.patient_code}`} className="p-4 flex flex-col justify-between">
              <div>
                <div className="flex items-start gap-3">
                  <Avatar
                    className="h-11 w-11 cursor-pointer"
                    onClick={() => navigate(`/patients/${v.patient_id}`)}
                  >
                    <AvatarFallback className="bg-bk-lightGreen text-bk-green font-semibold">
                      {initials(v.patient_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-slate-900 truncate">{v.patient_name}</p>
                      <StatusBadge status={v.status} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">{v.problem || "Home Physiotherapy"}</p>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{v.address || "Address not provided"}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs flex-wrap">
                      <span className="text-slate-600 font-medium tabular-nums">Fee {inr(v.visit_fee)}</span>
                      <AccountBalanceBadge balance={v.account_balance || 0} />
                      {user?.role === "admin" && (
                        <span className="text-slate-500 font-medium">· {v.physio_name}</span>
                      )}
                    </div>
                  </div>
                </div>

                {v.status === "provider_cancelled" && v.reassignment_needed && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-center justify-between">
                    <span>Cancelled by physio: {v.provider_cancellation_reason}</span>
                    <Badge variant="destructive" className="text-[10px]">Reassignment Needed</Badge>
                  </div>
                )}

                {/* Contact Shortcuts */}
                <div className="grid grid-cols-4 gap-1.5 mt-3">
                  <a href={telLink(v.phone)}>
                    <Button variant="outline" size="sm" className="w-full h-8 text-bk-blue text-xs">
                      <Phone className="h-3.5 w-3.5 mr-1" /> Call
                    </Button>
                  </a>
                  <a href={waLink(v.whatsapp || v.phone)} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="w-full h-8 text-bk-green text-xs">
                      <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                    </Button>
                  </a>
                  <a
                    href={v.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.address || "Ranchi")}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="outline" size="sm" className="w-full h-8 text-indigo-600 text-xs" data-testid={`navigate-${v.patient_code}`}>
                      <Navigation className="h-3.5 w-3.5 mr-1" /> Map
                    </Button>
                  </a>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate(`/patients/${v.patient_id}`)}>
                    Profile
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-wrap gap-1.5">
                {v.status === "scheduled" && (
                  <Button
                    size="sm"
                    disabled={busy === v.id}
                    onClick={() => handleStart(v)}
                    data-testid={`start-visit-${v.patient_code}`}
                    className="flex-1 h-8 bg-bk-blue hover:bg-blue-700 text-white text-xs"
                  >
                    {busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><PlayCircle className="h-3.5 w-3.5 mr-1" /> Start Visit</>}
                  </Button>
                )}

                {(v.status === "started" || v.status === "scheduled") && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setCompleteVisit(v);
                      setCompleteForm({
                        pain_score: 5,
                        treatment: "",
                        modalities: [],
                        duration: 45,
                        exercises: "",
                        notes: "",
                        next_visit: "",
                      });
                    }}
                    data-testid={`complete-visit-${v.patient_code}`}
                    className="flex-1 h-8 bg-bk-green hover:bg-emerald-700 text-white text-xs"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete
                  </Button>
                )}

                {v.status !== "completed" && v.status !== "provider_cancelled" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setProviderCancelVisit(v);
                      setCancelReason("");
                      setCancelReschedDate("");
                      setCancelReplacementPhysio("");
                    }}
                    className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Provider Cancel
                  </Button>
                )}

                {v.status === "scheduled" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePatientUnavailable(v)}
                    className="h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                  >
                    Unavailable
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPayVisit(v);
                    setPayAmount(String(v.visit_fee || 500));
                    setPayMethod("cash");
                    setPayType("session");
                  }}
                  data-testid={`visit-payment-${v.patient_code}`}
                  className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                >
                  <Wallet className="h-3.5 w-3.5 mr-1" /> Collect ₹
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* 1. Complete Visit Modal */}
      <Dialog open={!!completeVisit} onOpenChange={(open) => !open && setCompleteVisit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-green">
              <CheckCircle2 className="h-5 w-5" /> Complete Visit & Record Treatment
            </DialogTitle>
          </DialogHeader>
          {completeVisit && (
            <div className="space-y-3 py-2">
              <div className="p-3 bg-slate-50 border rounded-lg text-xs space-y-1">
                <p className="font-bold text-slate-800">{completeVisit.patient_name} ({completeVisit.patient_code})</p>
                <p className="text-slate-600">
                  Fee: {inr(completeVisit.visit_fee)} · Completing this visit will debit account balance or consume 1 package session.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Pain Score (0-10)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={completeForm.pain_score}
                    onChange={(e) => setCompleteForm({ ...completeForm, pain_score: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Duration (Minutes)</Label>
                  <Input
                    type="number"
                    value={completeForm.duration}
                    onChange={(e) => setCompleteForm({ ...completeForm, duration: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Treatment Given</Label>
                <Input
                  placeholder="e.g. Lumbar traction, core activation, stretching"
                  value={completeForm.treatment}
                  onChange={(e) => setCompleteForm({ ...completeForm, treatment: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Modalities Used</Label>
                <div className="flex flex-wrap gap-1.5">
                  {MODALITIES.map((mod) => {
                    const sel = completeForm.modalities.includes(mod);
                    return (
                      <button
                        key={mod}
                        type="button"
                        onClick={() => {
                          const mods = sel
                            ? completeForm.modalities.filter((m) => m !== mod)
                            : [...completeForm.modalities, mod];
                          setCompleteForm({ ...completeForm, modalities: mods });
                        }}
                        className={`px-2 py-1 rounded text-xs border ${
                          sel ? "bg-bk-green text-white border-bk-green" : "bg-white text-slate-600 border-slate-200"
                        }`}
                      >
                        {mod}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Home Exercises Prescribed</Label>
                <Input
                  placeholder="e.g. Cat-camel 10x, bridging 10x"
                  value={completeForm.exercises}
                  onChange={(e) => setCompleteForm({ ...completeForm, exercises: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Next Recommendation</Label>
                  <Input
                    type="date"
                    value={completeForm.next_visit}
                    onChange={(e) => setCompleteForm({ ...completeForm, next_visit: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Input
                    placeholder="Patient response / feedback"
                    value={completeForm.notes}
                    onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteVisit(null)}>Cancel</Button>
            <Button
              onClick={handleCompleteSubmit}
              disabled={busy === completeVisit?.id}
              className="bg-bk-green hover:bg-emerald-700 text-white"
            >
              {busy === completeVisit?.id ? "Saving..." : "Confirm Completion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2. Provider Cancel Modal */}
      <Dialog open={!!providerCancelVisit} onOpenChange={(open) => !open && setProviderCancelVisit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" /> Provider Cancellation
            </DialogTitle>
          </DialogHeader>
          {providerCancelVisit && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-600">
                Marking cancellation for <strong>{providerCancelVisit.patient_name}</strong>. No charge or penalty will be applied to the patient.
              </p>

              <div className="space-y-1.5">
                <Label>Cancellation Reason *</Label>
                <Input
                  placeholder="e.g. Physiotherapist unwell / Emergency / Bike breakdown"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>

              {user?.role === "admin" && (
                <div className="space-y-1.5">
                  <Label>Assign Replacement Physiotherapist (Optional)</Label>
                  <Select value={cancelReplacementPhysio} onValueChange={setCancelReplacementPhysio}>
                    <SelectTrigger><SelectValue placeholder="Leave blank to flag for reassignment" /></SelectTrigger>
                    <SelectContent>
                      {physios.map((ph) => (
                        <SelectItem key={ph.id} value={ph.id}>
                          {ph.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Reschedule to Date (Optional)</Label>
                <Input
                  type="date"
                  value={cancelReschedDate}
                  onChange={(e) => setCancelReschedDate(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProviderCancelVisit(null)}>Cancel</Button>
            <Button
              onClick={handleProviderCancelSubmit}
              disabled={busy === providerCancelVisit?.id}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy === providerCancelVisit?.id ? "Cancelling..." : "Confirm Cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Payment Modal */}
      <Dialog open={!!payVisit} onOpenChange={(open) => !open && setPayVisit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-green">
              <Wallet className="h-5 w-5" /> Collect Payment / Add Money
            </DialogTitle>
          </DialogHeader>
          {payVisit && (
            <div className="space-y-3 py-2">
              <div className="p-3 bg-slate-50 border rounded-lg text-xs space-y-1">
                <p className="font-bold text-slate-800">{payVisit.patient_name}</p>
                <p className="text-slate-600">
                  Account Balance: <AccountBalanceBadge balance={payVisit.account_balance || 0} />
                </p>
                <p className="text-[11px] text-slate-500">
                  Collected money will be credited to patient's account and attributed to you.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Payment Mode</Label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI / QR</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Payment Type</Label>
                  <Select value={payType} onValueChange={setPayType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="session">Session Payment</SelectItem>
                      <SelectItem value="account_credit">Add to Account</SelectItem>
                      <SelectItem value="package">Package Purchase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayVisit(null)}>Cancel</Button>
            <Button
              onClick={handlePaySubmit}
              disabled={payLoading}
              className="bg-bk-green hover:bg-emerald-700 text-white"
            >
              {payLoading ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
